import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminPricingConfig() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [syncing, setSyncing] = useState({});
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const isAdmin = user && (user.role === "admin" || user.is_owner);
  const isOwner = user && user.is_owner;

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/membership/tiers");
      setTiers(data.tiers || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const patchField = (slug, field, value) => {
    setTiers((prev) => prev.map((t) => (t.slug === slug ? { ...t, [field]: value } : t)));
  };

  const save = async (slug) => {
    const t = tiers.find((x) => x.slug === slug);
    if (!t) return;
    setSaving({ ...saving, [slug]: true }); setErr(""); setMsg("");
    try {
      const patch = {
        slug,
        price_monthly_aud: t.price_monthly_aud === "" || t.price_monthly_aud == null ? null : parseFloat(t.price_monthly_aud),
        price_annual_aud: t.price_annual_aud === "" || t.price_annual_aud == null ? null : parseFloat(t.price_annual_aud),
        onboarding_fee_aud: t.onboarding_fee_aud === "" || t.onboarding_fee_aud == null ? null : parseFloat(t.onboarding_fee_aud),
        stripe_price_id_monthly: t.stripe_price_id_monthly || null,
        stripe_price_id_annual: t.stripe_price_id_annual || null,
      };
      await api.patch(`/admin/membership/tiers/${slug}`, patch);
      setMsg(`${t.name} pricing saved.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving({ ...saving, [slug]: false });
    }
  };

  const syncStripe = async (slug) => {
    if (!window.confirm(`Create/refresh Stripe products & prices for the ${slug.toUpperCase()} tier?\nThis will create AUD recurring prices in Stripe based on the values above.`)) return;
    setSyncing({ ...syncing, [slug]: true }); setErr(""); setMsg("");
    try {
      const { data } = await api.post(`/admin/membership/tiers/${slug}/sync-to-stripe`);
      setMsg(`${slug.toUpperCase()} synced to Stripe. Product: ${data.product_id.slice(0, 18)}…`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing({ ...syncing, [slug]: false });
    }
  };

  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <a href="/projects" className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white">Back</a>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-pricing-config">
      <div className="mb-4">
        <Link to="/admin/membership-applications" className="text-xs text-brand-teal hover:underline">← Back to admin</Link>
      </div>
      <header className="mb-4">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Admin · ColourMatch Pro</div>
        <h1 className="text-2xl font-bold text-brand-navy">Pricing configuration</h1>
        <p className="text-sm text-gray-600">Edit monthly & annual pricing for each tier. Prices sync to Stripe as recurring AUD prices with lookup keys. You can also paste Stripe Price IDs manually.</p>
      </header>

      {msg && <div className="mb-4 p-3 rounded-lg bg-brand-success/10 border border-brand-success/40 text-sm text-brand-success" data-testid="pricing-msg">{msg}</div>}
      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="pricing-err">{String(err)}</div>}

      {loading && <div className="p-6 text-gray-600">Loading tiers…</div>}
      {!loading && tiers.map((t) => (
        <section key={t.slug} className="mb-6 bg-white border border-brand-border rounded-2xl p-5" data-testid={`tier-config-${t.slug}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-brand-navy">{t.name}</h2>
              <p className="text-xs text-gray-600">{t.positioning}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => save(t.slug)} disabled={!!saving[t.slug]} className="px-3 py-1.5 rounded-full text-xs bg-brand-navy text-white font-semibold hover:bg-brand-navy/90 disabled:opacity-50" data-testid={`save-${t.slug}`}>
                {saving[t.slug] ? "Saving…" : "Save"}
              </button>
              {isOwner && (
                <button onClick={() => syncStripe(t.slug)} disabled={!!syncing[t.slug]} className="px-3 py-1.5 rounded-full text-xs bg-brand-teal text-white font-semibold hover:bg-brand-teal/90 disabled:opacity-50" data-testid={`sync-${t.slug}`}>
                  {syncing[t.slug] ? "Syncing…" : "Sync to Stripe"}
                </button>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <PriceField label="Monthly price (AUD)" value={t.price_monthly_aud} onChange={(v) => patchField(t.slug, "price_monthly_aud", v)} testid={`price-monthly-${t.slug}`} />
            <PriceField label="Annual price (AUD)" value={t.price_annual_aud} onChange={(v) => patchField(t.slug, "price_annual_aud", v)} testid={`price-annual-${t.slug}`} />
            <PriceField label="Onboarding fee (AUD, optional)" value={t.onboarding_fee_aud} onChange={(v) => patchField(t.slug, "onboarding_fee_aud", v)} testid={`price-onboarding-${t.slug}`} />
            <div />
            <IdField label="Stripe Price ID · monthly" value={t.stripe_price_id_monthly} onChange={(v) => patchField(t.slug, "stripe_price_id_monthly", v)} testid={`price-id-monthly-${t.slug}`} />
            <IdField label="Stripe Price ID · annual" value={t.stripe_price_id_annual} onChange={(v) => patchField(t.slug, "stripe_price_id_annual", v)} testid={`price-id-annual-${t.slug}`} />
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Owner-managed. All values live in the <code>membership_tiers</code> collection. Sync to Stripe auto-creates recurring AUD prices under lookup keys <code>colourmatch_{t.slug}_monthly</code> / <code>colourmatch_{t.slug}_annual</code>.
          </div>
        </section>
      ))}

      <div className="mt-6 p-4 bg-brand-cream border border-brand-border rounded-xl text-xs text-gray-700">
        <strong>Reminder:</strong> Painter data is never deleted based on subscription state. Suspension only means read-only workspace + directory hidden. Cancellation is one-click for the painter and preserves everything.
      </div>
    </div>
  );
}

function PriceField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-widest text-brand-navy mb-1">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-2 text-gray-500 text-sm">A$</span>
        <input type="number" step="0.01" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="w-full pl-8 pr-3 py-2 border border-brand-border rounded-lg text-sm" data-testid={testid} />
      </div>
    </label>
  );
}
function IdField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-widest text-brand-navy mb-1">{label}</span>
      <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        placeholder="price_… (auto-populated after Sync)" className="w-full px-3 py-2 border border-brand-border rounded-lg text-xs font-mono" data-testid={testid} />
    </label>
  );
}
